import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";

/**
 * Compact A2P 10DLC status banner shown at the top of the Dispatcher portal.
 * Polls /api/admin/a2p-status every 5 minutes (backend itself caches the
 * upstream Twilio API call for 15 minutes, so this is cheap).
 *
 * States:
 *  - operational  → 🟢 SMS Fully Operational
 *  - pending      → 🟡 A2P approval pending — SMS limited to verified numbers
 *  - failed       → 🔴 A2P registration failed — action required
 *  - unknown      → grey · last_error tooltip
 */
export default function A2PStatusBanner() {
  const [data, setData] = useState(null);
  const [expanded, setExpanded] = useState(false);
  const [dismissed, setDismissed] = useState(
    typeof window !== "undefined" && window.sessionStorage?.getItem("a2p_banner_dismissed") === "1"
  );

  useEffect(() => {
    let timer;
    const load = async () => {
      try {
        const r = await api.get("/admin/a2p-status");
        setData(r.data || null);
      } catch {
        // silent — banner just stays in last state
      }
    };
    load();
    timer = setInterval(load, 5 * 60 * 1000);  // 5 min poll
    return () => clearInterval(timer);
  }, []);

  if (!data || dismissed) return null;

  // Hide the green banner after the user has seen it once per session.
  if (data.overall === "operational" && dismissed) return null;

  const palette = {
    operational: { bg: "bg-emerald-500/10", border: "border-emerald-500/40", dot: "bg-emerald-500", text: "text-emerald-300", label: "SMS Fully Operational" },
    pending:     { bg: "bg-amber-500/10",   border: "border-amber-500/40",   dot: "bg-amber-400 animate-pulse", text: "text-amber-200",   label: "A2P approval pending" },
    failed:      { bg: "bg-red-500/10",     border: "border-red-500/40",     dot: "bg-red-500",     text: "text-red-300",      label: "A2P registration failed" },
    unknown:     { bg: "bg-zinc-700/30",    border: "border-zinc-600",       dot: "bg-zinc-400",    text: "text-zinc-300",      label: "A2P status unknown" },
  };
  const p = palette[data.overall] || palette.unknown;

  return (
    <div data-testid="a2p-status-banner" className={`${p.bg} border ${p.border} rounded mx-4 md:mx-6 lg:mx-8 mt-3 mb-2`}>
      <div className="flex items-center justify-between px-4 py-2.5 gap-3">
        <div className="flex items-center gap-3 min-w-0 flex-1">
          <span className={`w-2 h-2 rounded-full flex-shrink-0 ${p.dot}`}/>
          <div className={`text-sm ${p.text} truncate`}>
            <strong>{p.label}</strong>
            {data.overall === "pending" && (
              <span className="ml-2 text-xs opacity-80 hidden sm:inline">
                · Customer SMS limited to Twilio-verified numbers until TCR vetting completes (1–3 business days). Outbound SMS to unregistered numbers may be filtered by carriers.
              </span>
            )}
            {data.overall === "operational" && (
              <span className="ml-2 text-xs opacity-80 hidden sm:inline">
                · All US carrier SMS delivering normally. Brand &amp; Campaign approved.
              </span>
            )}
            {data.overall === "failed" && (
              <span className="ml-2 text-xs opacity-80 hidden sm:inline">
                · Open Twilio Console → Messaging → Regulatory Compliance to resubmit.
              </span>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <button
            data-testid="a2p-details-toggle"
            onClick={() => setExpanded((v) => !v)}
            className={`text-[10px] uppercase tracking-[0.18em] ${p.text} opacity-70 hover:opacity-100 transition`}
          >
            {expanded ? "Hide" : "Details"}
          </button>
          {data.overall === "operational" && (
            <button
              data-testid="a2p-dismiss"
              onClick={() => {
                window.sessionStorage?.setItem("a2p_banner_dismissed", "1");
                setDismissed(true);
              }}
              className="text-xs opacity-60 hover:opacity-100 text-zinc-300"
              aria-label="Dismiss banner"
            >
              ×
            </button>
          )}
        </div>
      </div>
      {expanded && (
        <div className={`border-t ${p.border} px-4 py-3 text-xs ${p.text} space-y-1 font-mono opacity-90`}>
          <div>Brand:    <span className="opacity-80">{data.brand?.status || "—"}</span>{data.brand?.brand_score != null && <span className="opacity-60"> (score {data.brand.brand_score})</span>}</div>
          <div>Campaign: <span className="opacity-80">{data.campaign?.status || "—"}</span>{data.campaign?.use_case && <span className="opacity-60"> · {data.campaign.use_case}</span>}</div>
          <div>Last sync: {data.fetched_at ? new Date(data.fetched_at).toLocaleString() : "—"} {data.from_cache ? "· cached" : ""}</div>
          {data.last_error && <div className="text-red-300/80">Last error: {data.last_error}</div>}
          {data.reason && <div className="text-amber-300/80">{data.reason}</div>}
        </div>
      )}
    </div>
  );
}
